import { Box, Stack, Typography } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  useEffect,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { orderCatalogue, reorderIds } from "@shared/catalogue-order";
import type { Asset } from "@shared/types/content-api";
import { readAnyAsset } from "./useVodTitles";
import { foyerApi, type CarouselPreviewMatch } from "../../http/foyer";
import {
  imageSrc,
  resolveChannelLogoImage,
  resolveVodThumbnail,
} from "@shared/vod-images";
import {
  carouselPageSize,
  type Carousel,
  type CarouselTitle,
} from "@shared/carousel";
import { orderChannels } from "@shared/channel-order";

/**
 * The official line-up, read once for the whole page.
 *
 * A module-level promise rather than per-instance state: the carousel editor
 * draws several previews at once, this is global config that changes about as
 * often as a channel is launched, and N previews firing N identical requests is
 * the kind of thing nobody notices until a list is long.
 *
 * Never rejects, and never THROWS: the call itself is guarded, not just the
 * promise. A rejected promise was the obvious half and it is the half that does
 * not fire when the method is missing entirely — which is exactly what a
 * partial test double looks like, and what took the whole editor dialog down
 * with it rather than degrading one rail.
 *
 * A preview that cannot read the order still draws the rail, in the catalogue's
 * order, which is what it did before this existed.
 */
let officialOrderPromise: Promise<string[]> | null = null;
function officialChannelOrder(): Promise<string[]> {
  officialOrderPromise ??= (async () => {
    try {
      return (await foyerApi.channelOrder()).channelOrder;
    } catch {
      return [];
    }
  })();
  return officialOrderPromise;
}

/**
 * Drop the cached line-up. **Tests only.**
 *
 * A module-level cache is right in the browser — the page is reloaded to change
 * a global setting — and wrong in a test file, where one module serves every
 * test in it and the first read would be handed to all of them. Exported rather
 * than worked around with `vi.resetModules()` because the hazard is a property
 * of this cache, so the escape hatch belongs next to it where the next person
 * writing a preview test will see it.
 */
export function resetOfficialChannelOrderCacheForTests(): void {
  officialOrderPromise = null;
}

/**
 * "Preview" — the rail as a television draws it (BE-28).
 *
 * The Figma mock labels this "Förhandsvisning"; the CMS ships in English like
 * every other page in it, so the copy here is English and the design's Swedish
 * is the designer's working language rather than a product decision.
 *
 * ## What it can and cannot promise
 *
 * It shows the AUTHORED rail: the posters in the operator's order, truncated at
 * the display limit, with the "Show all" card the limit exists to produce. It
 * deliberately does NOT pretend to show what a given room sees — availability,
 * hiding and re-ordering are per-room and are computed by `carouselsForRoom`
 * from entitlement this dialog has no room to evaluate against.
 *
 * Saying so is the point. A preview that silently rendered the admin's own view
 * as "the guest experience" is the class of lie the `availabilityStatus` model
 * exists to stop, so the caption states which one this is.
 *
 * ⚠️ **Channel ORDER is honoured; channel MEMBERSHIP past the limit is not.**
 * A channel rail is drawn in the official line-up order (see
 * `@shared/channel-order`), the same rule the server applies. But the picks are
 * truncated to `limit + 1` BEFORE they are read — one fetch per poster, which
 * is what keeps this to eleven cheap reads — so a channel the line-up would
 * pull to the front from beyond that cut is never fetched and cannot appear.
 * The server has the whole set in hand and orders before truncating, so it does
 * not have this gap. It bites only a rail longer than its own display limit,
 * and closing it means resolving the full pick list here, which is a bigger
 * change than the preview is worth until somebody hits it.
 *
 * ## Why it is dark
 *
 * Because the television is. A rail previewed on the CMS's white surface tells
 * an operator nothing about whether their poster art has enough contrast, which
 * is most of what a preview is for.
 */

export interface CarouselPreviewProps {
  carousel: Carousel;
  /** Which title to draw over the rail. Defaults to the first authored one. */
  language?: string;
  /**
   * Already-resolved rows, keyed by assetId, when the caller has them.
   *
   * The editor dialog resolves the picks once for the ordered list it draws
   * above this rail; handing them down means the preview does not fetch the
   * same titles a second time, and — the part that actually bites — the two
   * cannot show different content for the same carousel while one is still
   * in flight. Omit it and the preview resolves its own, which is what the
   * carousel LIST does.
   */
  byId?: Map<string, Asset>;
  /**
   * Whether the caller's `byId` is still filling.
   *
   * Without it a supplied-but-empty lookup renders as "No media picked yet."
   * while the rows are in flight — telling an operator their picks are gone at
   * exactly the moment they are being fetched. Ignored when `byId` is absent,
   * because then this component owns the loading state.
   */
  resolving?: boolean;
  /**
   * Makes the rail the ORDERING surface. Omit it and the preview is read-only,
   * which is what the carousel list wants.
   *
   * 🚨 When this is given the rail draws EVERY pick, not just the first
   * `displayLimit`. A reorder surface that only showed ten tiles could never
   * move the twenty-fifth, and an operator would have no way to know why —
   * the "Show all" card moves to where the limit falls instead, so the cut is
   * still visible and nothing is unreachable.
   */
  onReorder?: (assetIds: string[]) => void;
  /**
   * A dynamic rail's drag, which writes PINS rather than a pick list.
   *
   * Kept apart from {@link onReorder} because the two write different fields
   * for different reasons — `assetIds` IS a curated rail, while `assetOrder` is
   * a sparse overlay on a set nobody stored. One callback would have made the
   * caller guess which, and guessing wrong writes a pick list onto a rail that
   * has none.
   */
  onPin?: (assetIds: string[]) => void;
}

/**
 * 16:9, because that is what the catalogue is optimised to give us.
 *
 * ⚠️ It was 92×138 — portrait — and that was fighting the resolver. The image
 * preference order is explicitly built the other way: *"ordered by how well
 * each survives being drawn into a wide list tile: `thumbnail` and `cover` are
 * already that shape, `banner` only crops at the sides, and a portrait `poster`
 * crops hardest — so it is late rather than first."* So the preview asked for a
 * landscape-first image and then cropped it into a portrait box, which is the
 * worst of both: the wide art lost its sides, and the one type that WOULD have
 * fitted was the one deliberately ranked late.
 *
 * A channel makes the mismatch total — its mark is a wide logo — and channel
 * rails are the common case now.
 */
const TILE = {
  width: 160,
  height: 90,
  flexShrink: 0,
  borderRadius: 1.5,
} as const;

/**
 * The tile image, at the tile's own shape.
 *
 * A channel resolves through the LOGO order and is contained rather than
 * cropped — the same rule the picker and the ordered list follow, so one title
 * does not change appearance as it moves between the three surfaces.
 */
function tileImage(asset: Asset, language: string): string | null {
  const input = {
    images: asset.images ?? [],
    language,
    languages: [language, "sv", "en"],
  };
  const render = { width: 320, height: 180 };
  if (asset.type === "channel") {
    const image = resolveChannelLogoImage(input);
    return image ? imageSrc(image, render) : null;
  }
  return resolveVodThumbnail(input, render);
}

/**
 * One tile, drawn identically wherever it appears.
 *
 * Shared by the curated rail and the dynamic-collection match on purpose: two
 * copies would eventually disagree about the channel-logo rule or the fallback,
 * and a preview whose two halves render the same asset differently is a preview
 * nobody can trust.
 */
/**
 * The rail, and — when `onReorder` is given — the surface an operator arranges
 * it on.
 *
 * Ruled by Sigge: *"have the user drag and drop the preview instead, that's
 * more natural"*. It is: the rail is what a guest meets, and arranging a row
 * while looking at the row needs no explaining.
 *
 * The pointer activator is on the TILE, not on a handle. That was the whole
 * defect the first attempt shipped — `CatalogueGrid` had already written down
 * that dragging the thing itself is *"what an operator tries first"* — and the
 * grip pattern is not repeated here because a poster IS its own handle.
 */
function ReorderableRail({
  assets,
  language,
  limit,
  totalPicks,
  onReorder,
}: {
  assets: Asset[];
  language: string;
  limit: number;
  totalPicks: number;
  onReorder?: (assetIds: string[]) => void;
  /**
   * A dynamic rail's drag, which writes PINS rather than a pick list.
   *
   * Kept apart from {@link onReorder} because the two write different fields
   * for different reasons — `assetIds` IS a curated rail, while `assetOrder` is
   * a sparse overlay on a set nobody stored. One callback would have made the
   * caller guess which, and guessing wrong writes a pick list onto a rail that
   * has none.
   */
  onPin?: (assetIds: string[]) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const showAll = (
    <Stack
      key="__show_all__"
      alignItems="center"
      justifyContent="center"
      spacing={1}
      sx={{ ...TILE, background: "rgba(255,255,255,.12)" }}
    >
      <MoreHorizIcon sx={{ color: "#fff" }} />
      <Typography sx={{ color: "#fff", fontSize: 11 }}>Show all</Typography>
    </Stack>
  );

  if (!onReorder) {
    return (
      <Stack direction="row" spacing={1.5} sx={{ overflowX: "auto", pb: 1 }}>
        {assets.map((asset) => (
          <PreviewTile key={asset.assetId} asset={asset} language={language} />
        ))}
        {/* The "Show all" card is what the display limit is FOR — it appears
            exactly when there is more behind the rail than the limit shows. */}
        {totalPicks > limit && showAll}
      </Stack>
    );
  }

  const ids = assets.map((a) => a.assetId);
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) return;
    const next = reorderIds(ids, String(active.id), String(over.id));
    // `reorderIds` returns null on a no-op or an unknown id, so a stray drop
    // cannot write a spurious order.
    if (next) onReorder(next);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={ids} strategy={horizontalListSortingStrategy}>
        <Stack direction="row" spacing={1.5} sx={{ overflowX: "auto", pb: 1 }}>
          {assets.map((asset, index) => (
            <SortablePreviewTile
              key={asset.assetId}
              asset={asset}
              language={language}
              // Everything past the limit still draws and still drags — it just
              // sits behind the "Show all" card on a television, and says so.
              beyondLimit={index >= limit}
            />
          ))}
          {/* Placed where the cut FALLS rather than at the end, because the
              rail now draws past it. */}
          {totalPicks > limit && showAll}
        </Stack>
      </SortableContext>
    </DndContext>
  );
}

function SortablePreviewTile({
  asset,
  language,
  beyondLimit,
}: {
  asset: Asset;
  language: string;
  beyondLimit: boolean;
}) {
  const sortable = useSortable({ id: asset.assetId });
  const onPointerDown = sortable.listeners?.onPointerDown as
    | ((e: ReactPointerEvent) => void)
    | undefined;
  return (
    <PreviewTile
      asset={asset}
      language={language}
      setNodeRef={sortable.setNodeRef}
      onPointerDown={onPointerDown}
      grip={{ ...sortable.attributes, ...sortable.listeners }}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.4 : beyondLimit ? 0.5 : undefined,
      }}
      hint={
        beyondLimit
          ? `${asset.originalTitle} — past the display limit, behind Show all`
          : undefined
      }
    />
  );
}

function PreviewTile({
  asset,
  language,
  setNodeRef,
  style,
  onPointerDown,
  grip,
  hint,
}: {
  asset: Asset;
  language: string;
  setNodeRef?: (el: HTMLElement | null) => void;
  style?: CSSProperties;
  onPointerDown?: (e: ReactPointerEvent) => void;
  grip?: Record<string, unknown>;
  hint?: string;
}) {
  const src = tileImage(asset, language);
  const draggable = Boolean(onPointerDown);
  return (
    <Box
      ref={setNodeRef}
      style={style}
      onPointerDown={onPointerDown}
      {...(draggable ? grip : {})}
      aria-label={draggable ? `Reorder ${asset.originalTitle}` : undefined}
      sx={{
        ...TILE,
        overflow: "hidden",
        background: "rgba(255,255,255,.08)",
        display: "flex",
        alignItems: "flex-end",
        ...(draggable && {
          cursor: "grab",
          // Without this a touch drag scrolls the rail instead of moving the
          // tile — dnd-kit cannot preventDefault a passive touch listener.
          touchAction: "none",
        }),
      }}
      title={hint ?? asset.originalTitle}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt={asset.originalTitle}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: asset.type === "channel" ? "contain" : "cover",
          }}
        />
      ) : (
        // Null is a real answer. A generated tile reads as "no artwork"; a
        // broken <img> reads as a bug in the CMS.
        <Typography
          sx={{
            color: "rgba(255,255,255,.75)",
            fontSize: 11,
            p: 1,
            lineHeight: 1.3,
          }}
        >
          {asset.originalTitle}
        </Typography>
      )}
    </Box>
  );
}

function titleFor(titles: CarouselTitle[], language?: string): string {
  if (titles.length === 0) return "";
  const wanted = language
    ? titles.find((t) => t.language?.toLowerCase() === language.toLowerCase())
    : undefined;
  return (wanted ?? titles[0])?.text ?? "";
}

export default function CarouselPreview({
  carousel,
  language,
  byId,
  resolving,
  onReorder,
  onPin,
}: CarouselPreviewProps) {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [official, setOfficial] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const ids = carousel.assetIds ?? [];
  const limit = carouselPageSize("CAROUSEL", carousel.displayLimit);
  // Fetch one MORE than the rail draws: that extra row is the whole question
  // the "Show all" card answers, and asking for it is cheaper than asking for
  // a count the catalogue does not offer.
  /**
   * As the ORDERING surface it needs every pick; as a read-only rail it needs
   * one more than it draws (that extra row is the whole question the "Show all"
   * card answers, and asking for it is cheaper than asking for a count the
   * catalogue does not offer).
   *
   * The dialog supplies `byId` for the whole picked set anyway, so the wide
   * case costs nothing there. The narrow case is the carousel LIST, which
   * resolves its own and must stay bounded.
   */
  const wanted = onReorder ? ids : ids.slice(0, limit + 1);
  // Joined, so the effect re-runs when the PICKS change and not when the array
  // identity does — this component re-renders on every keystroke in the dialog.
  const key = wanted.join(",");

  // A supplied lookup replaces the fetch entirely — including the loading
  // state, because the caller is already showing one for the same read.
  const supplied = byId
    ? wanted.flatMap((id) => {
        const row = byId.get(id);
        return row ? [row] : [];
      })
    : null;

  /**
   * A dynamic collection's match, resolved against the CATALOGUE.
   *
   * 🚨 Not what any room sees, and the caption below says so. The builder is
   * global and holds no premise, so there is no entitlement to narrow by —
   * this is "what could this rail ever contain", which is the question an
   * author is actually asking. A premise holding fewer of these channels shows
   * fewer, and that gap is the note rather than a silent difference.
   */
  const [match, setMatch] = useState<CarouselPreviewMatch | null>(null);
  const [matching, setMatching] = useState(false);
  // Joined, because this component re-renders on every keystroke in the dialog
  // while the FILTER changes rarely.
  const filterKey =
    carousel.type === "query"
      ? [
          carousel.featureSetId ?? "",
          (carousel.tagIds ?? []).join("|"),
          (carousel.assetTypeFilter ?? []).join("|"),
          carousel.sourceType ?? "",
        ].join("~")
      : "";

  useEffect(() => {
    let cancelled = false;
    void officialChannelOrder().then((o) => {
      if (!cancelled) setOfficial(o);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (carousel.type !== "query") {
      setMatch(null);
      return;
    }
    let cancelled = false;
    setMatching(true);
    const [featureSetId, tags, types, sourceType] = filterKey.split("~");
    void foyerApi
      .previewCarousel({
        featureSetId: featureSetId || undefined,
        tagIds: tags ? tags.split("|") : [],
        assetTypeFilter: types ? types.split("|") : [],
        sourceType: sourceType || undefined,
      })
      // A failed match shows the empty state, never a stale one: a preview
      // still drawing the previous filter's tiles is worse than an empty box.
      .catch(() => null)
      .then((res) => {
        if (cancelled) return;
        setMatch(res);
        setMatching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [carousel.type, filterKey]);

  useEffect(() => {
    let cancelled = false;
    if (byId || carousel.type !== "curated" || wanted.length === 0) {
      setAssets([]);
      return;
    }
    setLoading(true);
    void (async () => {
      // One read per poster. The list route offers no `assetId` filter — the
      // catalogue's own filter exists but Foyer's list contract does not expose
      // it, and inventing a query parameter for a preview would be a wider
      // change than the preview is worth. Bounded by `limit + 1`, so this is at
      // most eleven cheap reads and never grows with the rail.
      // `readAnyAsset`, not `getVodTitle` — the VOD point read 404s a channel
      // on purpose, and this path is the one the carousel LIST uses, so
      // without the fallback a channel rail previews as entirely missing.
      const results = await Promise.all(wanted.map((id) => readAnyAsset(id)));
      if (cancelled) return;
      // Authored order, preserved by construction — `wanted` IS the order.
      setAssets(results.filter((a): a is Asset => Boolean(a)));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, carousel.type, Boolean(byId)]);

  // `supplied` wins when the caller passed one — `assets` is only ever
  // populated by the fetch this component owns, and that fetch does not run.
  const resolved = supplied ?? assets;

  /**
   * 🚨 The preview draws its own list, so it needs the ordering too.
   *
   * The server orders channel rails in `carouselResolver`; this component
   * resolves and draws its own assets, so before this the CMS showed the
   * catalogue's arbitrary answer while a television showed the line-up. Two
   * paths that must agree and had no shared rule — reported by Sigge as
   * *"I did a line-up. works perfect. But when I preview a carousel it ignores
   * that"*.
   *
   * Same module the server uses, so there is one opinion rather than two that
   * agree by luck. No premise override here: this is the GLOBAL editor and
   * there is no hotel in scope, so a preview shows the national order — which
   * is what every hotel sees until it moves something itself.
   *
   * ⚠️ **This component has TWO render paths and both must call this.** The
   * curated rail draws resolved picks; a `query` rail draws `match.assets` from
   * the filter preview. The first fix ordered only the curated one and the bug
   * was reported a second time, unchanged, because a product-driven channel
   * rail is a query. One helper, both call sites — a third path must call it
   * too.
   *
   * Non-channel rails are returned untouched: a film rail's order is the
   * operator's curated arrangement or the catalogue's relevance, and neither
   * wants a national channel line-up applied to it.
   */
  const inLineupOrder = (assets: readonly Asset[]): Asset[] =>
    // ⚠️ `curated` is excluded, mirroring the resolver: the line-up fills a gap
    // where nobody chose an order, and a curated rail is the case where
    // somebody did. Previewing a national line-up over an arrangement the
    // television now honours would be the preview lying in the other
    // direction.
    carousel.type !== "curated" && assets.some((a) => a.type === "channel")
      ? orderChannels(
          assets,
          (a) => ({
            id: a.assetId,
            title:
              typeof a.originalTitle === "string" ? a.originalTitle : a.assetId,
          }),
          { official }
        )
      : [...assets];

  /**
   * Everything a curated rail holds can be arranged, channels included.
   *
   * ⚠️ This was briefly gated on "no channels", because the resolver re-sorted
   * any rail holding one into the national line-up — a drag there would have
   * arranged something the television then ignored. Sigge ruled the other way
   * (*"I want the TV to show it the way the carousel is created"*), the
   * resolver now honours an authored order, and so the gate is gone. Both
   * halves moved together on purpose: a drag is only honest while the read path
   * keeps what it writes.
   */
  const reorderable = Boolean(onReorder);

  // Ordered BEFORE the slice, so the display limit truncates the arranged list
  // rather than the arbitrary one.
  const ordered = inLineupOrder(resolved);
  /**
   * A reorder surface draws EVERY pick; a read-only one truncates.
   *
   * Ten tiles could never move the twenty-fifth, and an operator would have no
   * way to see why. The "Show all" card moves to where the limit falls instead,
   * so the cut stays visible and nothing is out of reach.
   */
  const shown = reorderable ? ordered : ordered.slice(0, limit);
  /**
   * The same overlay the resolver applies, so what an author drags is what a
   * television draws. Sparse on purpose: a dynamic rail's members are computed,
   * so the pins name only what somebody moved and everything else keeps its
   * place behind them.
   */
  const matched = match
    ? (carousel.assetOrder ?? []).length
      ? orderCatalogue(
          inLineupOrder(match.assets).map((a) => ({ id: a.assetId, asset: a })),
          carousel.assetOrder
        ).map((e) => e.asset)
      : inLineupOrder(match.assets)
    : [];
  const heading =
    titleFor(carousel.title ?? [], language) || carousel.name || "";

  return (
    <Box
      sx={{
        // The television, not the CMS. Fixed dark in both themes on purpose:
        // this surface is a simulation of a panel, not part of the chrome.
        background: "#0B1020",
        borderRadius: 2,
        p: 2.5,
        overflow: "hidden",
      }}
    >
      <Typography
        sx={{ color: "#fff", fontWeight: 700, fontSize: 15, mb: 1.5 }}
      >
        {heading || "Untitled carousel"}
      </Typography>

      {carousel.type === "epg" ? (
        <Typography sx={{ color: "rgba(255,255,255,.6)", fontSize: 13, py: 3 }}>
          A schedule carousel fetches no titles — the app draws the schedule
          itself from the channel, so there is nothing to preview here.
        </Typography>
      ) : carousel.type === "query" ? (
        matching ? (
          <Typography
            sx={{ color: "rgba(255,255,255,.5)", fontSize: 13, py: 3 }}
          >
            Matching…
          </Typography>
        ) : !match || match.assets.length === 0 ? (
          <Typography
            sx={{ color: "rgba(255,255,255,.6)", fontSize: 13, py: 3 }}
          >
            Nothing in the catalogue matches this filter yet.
          </Typography>
        ) : (
          <ReorderableRail
            assets={matched}
            language={language ?? "sv"}
            // No "Show all" cut to mark here: the match is already capped by
            // the preview route, and a display limit is a TELEVISION concern
            // over a set this screen is not showing in full anyway.
            limit={matched.length}
            totalPicks={matched.length}
            onReorder={onPin}
          />
        )
      ) : shown.length === 0 ? (
        <Typography sx={{ color: "rgba(255,255,255,.5)", fontSize: 13, py: 3 }}>
          {(byId ? resolving : loading) ? "Loading…" : "No media picked yet."}
        </Typography>
      ) : (
        <ReorderableRail
          assets={shown}
          language={language ?? "sv"}
          limit={limit}
          totalPicks={ids.length}
          onReorder={reorderable ? onReorder : undefined}
        />
      )}

      <Typography
        sx={{ color: "rgba(255,255,255,.45)", fontSize: 11, mt: 1.5 }}
      >
        {carousel.type === "query" && match ? (
          <>
            {/* The count is the server's own, never the length of what is
                drawn — the cap must not read as the answer. */}
            {match.assets.length < match.totalCount
              ? `Showing ${match.assets.length} of ${match.totalCount} matches. `
              : `${match.totalCount} ${match.totalCount === 1 ? "match" : "matches"}. `}
            {match.truncated &&
              "This product is defined by a rule and the list above is a prefix, so there may be more. "}
            {/* 🚨 The note Sigge asked for, and the honest half of showing a
                catalogue-wide match on a screen that has no premise. */}
            Every hotel sees only what it holds — a premise without all of these
            channels shows fewer, and one with none of them shows no row at all.
          </>
        ) : (
          <>
            {reorderable ? "Drag a tile to set the order. " : ""}
            This is how the carousel is built. What any one room actually sees
            is decided by that room’s entitlement when its screen asks for it.
          </>
        )}
      </Typography>
    </Box>
  );
}
